import React from 'react';
import { NoteIcon, Pencil } from 'assets/icons';
import Button from 'components/button';
import LastUpdateIcon from 'components/lastUpdateIcon/LastUpdateIcon';

// The three colours come from the design and are used nowhere else, so they
// stay arbitrary values rather than entering the Tailwind palette; promote
// them if a second component ever needs the same set.
//
// Only the icon reads this constant — a colour prop, not a class. The title's
// class spells the same value out literally, because Tailwind scans source
// files for complete class strings and would never generate `text-[${...}]`.
export const POST_IT_TITLE_COLOR = '#8D7F3C';

/**
 * The company note, rendered identically wherever it appears: the company
 * sheet, the timesheet detail and the mission contract view.
 *
 * Renders nothing when the note is empty — the card is never an empty shell.
 * `onEdit` is what separates the writable copy in the company sheet from the
 * read-only ones: omit it and the card carries neither pencil nor click.
 */
/**
 * `note` is the company's `post_it` sub-document. The author is stamped on it
 * at save time rather than resolved from the change log, because the note is
 * served by three endpoints with three different projections and none of them
 * populates a user.
 *
 * A note pulled from BeSTT has no author — nobody here wrote it — so it is
 * dated by its import and the wording drops the « par … » on its own.
 */
const PostIt = ({ note, companyName, onEdit, className = '' }) => {
  const text = (note?.content ?? '').trim();
  if (!text) return null;

  // A note pulled from BeSTT has no author and no edit of ours, so it is dated
  // by its import instead.
  const lastUpdate = {
    at: note?.updated_at ?? note?.imported_at,
    by: note?.updated_by,
  };

  // The whole note opens the editor, not just the pencil — but only where the
  // note is writable. The read-only copies on the timesheet and the contract
  // get no onEdit, so they stay plain text.
  const editable = Boolean(onEdit);

  return (
    <div
      {...(editable && {
        role: 'button',
        tabIndex: 0,
        onClick: onEdit,
        onKeyDown: (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          // Space would scroll the page the note sits on.
          event.preventDefault();
          onEdit();
        },
      })}
      className={`bg-[#FFF3B0] border border-solid border-[#F0E18C] rounded p-3 flex gap-2 ${
        editable ? 'cursor-pointer hover:bg-[#FBE99A]' : ''
      } ${className}`}
    >
      <div className={'flex flex-col gap-1 min-w-0 flex-1'}>
        <div className={'flex gap-1.5 items-center text-[#8D7F3C] ft-sm'}>
          <NoteIcon wh={16} color={POST_IT_TITLE_COLOR} />
          <span className={'uppercase'}>
            Post-it{companyName ? ` · ${companyName}` : ''}
          </span>
        </div>
        {/* whitespace-pre-line, not a rich text renderer: the note is stored as
            plain text and its line breaks are the only formatting it carries. */}
        <div className={'text-[#5C4D0A] whitespace-pre-line break-words'}>
          {text}
        </div>
      </div>
      {/* The date moves into the tooltip, as on every card of the section.
          Read-only copies keep it: the icon is the only thing they carry on
          the right, since they have no pencil. */}
      <div className={'shrink-0 flex items-center gap-2'}>
        <LastUpdateIcon lastUpdate={lastUpdate} color={POST_IT_TITLE_COLOR} />
        {onEdit && (
          <Button
            size={'SMI'}
            color={'DEFAULT'}
            // The card behind fires the same action: without this the click
            // would run it twice, opening and closing the modal in one go.
            onClick={(event) => {
              event.stopPropagation();
              onEdit();
            }}
          >
            <Pencil wh={20} color={'#111827'} />
          </Button>
        )}
      </div>
    </div>
  );
};

export default PostIt;
